import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import TopBar from "../../Components/AddRelationshipTopBar";
import "./style.css";

/**
 * Chrome shared by the Capture screens (Figma 9856:16314).
 *
 * The hub is a home tab and keeps the global header; every step after it is a
 * focused flow with its own back / close control, so the global header stands
 * down there (restored on unmount) — same reasoning as the Explore sub-pages.
 */
export const useFocusedCaptureHeader = () => {
  const { setHeaderContent, setHeaderContentRightSideContent } = useHeader();
  useEffect(() => {
    setHeaderContent(false);
    setHeaderContentRightSideContent(false);
    return () => {
      setHeaderContent(true);
      setHeaderContentRightSideContent(true);
    };
  }, [setHeaderContent, setHeaderContentRightSideContent]);
};

/**
 * The app-standard page header — the same TopBar My Connections, the Explore
 * and the Journey sub-pages use: back arrow hard-left at the screen's edge,
 * title centred. `onBack` wins over `to`. The Figma frames put a close "X"
 * top-right on the processing steps; on web every step keeps the one back
 * control in the same place, so the layout matches the rest of the app.
 */
export function CaptureHeader({ title, to = "/capture", onBack }) {
  const navigate = useNavigate();
  return (
    <div className="cap-topbar">
      <TopBar
        title={title}
        showBackButton
        useRegularArrow
        onBackClick={() => (onBack ? onBack() : navigate(to))}
      />
    </div>
  );
}

/**
 * `focused` — a step after the hub: on a desktop its content sits in the
 * white rounded card My Connections uses; on a phone it stays the Figma
 * mobile layout.
 */
export function CapturePage({ children, className = "", focused = false }) {
  return (
    <div className={`cap-page${focused ? " cap-page--focused" : ""} ${className}`}>
      <div className="cap-page__inner">{children}</div>
    </div>
  );
}

/** A bottom sheet on a phone, a centred card on a wider screen. */
export function CaptureSheet({ open, title, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="cap-sheet" role="presentation" onClick={onClose}>
      <div
        className="cap-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="cap-sheet__grabber" aria-hidden="true" />
        {title && <h2 className="cap-sheet__title">{title}</h2>}
        {children}
      </div>
    </div>
  );
}

/** A stable, per-mount idempotency key: retries of one tap reuse it. */
export const makeIdempotencyKey = (method) =>
  `web-${method.toLowerCase()}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
